import React, { createContext, useContext, useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { coachApi, setCSRFToken } from '../api/client';

export type Role = 'owner' | 'admin' | 'branch_head' | 'teacher' | 'counselor' | 'accountant' | 'student' | 'parent';

export interface Ward {
  student_id: string;
  admission_no: string;
  full_name: string;
  avatar_url?: string;
  batch_name?: string;
  is_primary?: boolean;
}

export interface FamilyContext {
  active_student_id: string;
  wards: Ward[];
}

export interface User {
  id: string;
  email: string;
  name?: string;
  role?: Role;
  tenant_id?: string;
  branch_id?: string;
  family_context?: FamilyContext;
}

export interface Tenant {
  id: string;
  slug: string;
  name: string;
  mode?: string;
}

export interface AuthState {
  user: User | null;
  tenant: Tenant | null;
  role: Role;
  setRole: (r: Role) => void;
  permissions: Record<string, boolean>;
  enabledSuites: string[];
  enabledModules: string[];
  planTier: string;
  isAuthenticated: boolean;
  isLoading: boolean;
  familyContext?: FamilyContext;
  activeStudent?: Ward;
  switchActiveStudent: (studentId: string) => void;
  login: (email: string, pass: string) => Promise<void>;
  logout: () => Promise<void>;
  hasPermission: (perm: string) => boolean;
  hasSuite: (suiteId: string) => boolean;
  hasModule: (suiteId: string, moduleId?: string) => boolean;
}

interface MeResponse {
  user_id: string;
  email: string;
  full_name?: string;
  role?: Role;
  permissions: Record<string, boolean>;
  tenant?: Tenant;
  enabled_suites?: string[];
  enabled_modules?: string[];
  plan_tier?: string;
  family_context?: FamilyContext;
}

export const ROLE_DOWN_SCOPE_CAPABILITIES: Record<Role, string[]> = {
  owner: ['*'],
  admin: ['*'],
  branch_head: ['*'],
  teacher: [
    'core.dashboard.read',
    'core.directory.read',
    'core.batches.read',
    'core.schedule.manage',
    'core.study.read',
    'core.study.manage',
    'core.attendance.read',
    'core.attendance.manage',
    'core.notices.read',
    'core.notices.manage',
    'students.read',
    'sis.tuition.use',
    'schedule.read',
    'sessions.manage',
    'attendance.mark',
    'attendance.read',
    'courses.read',
    'courses.manage',
    'tests.author',
    'tests.grade',
    'live.host',
    'live.join',
    'proctoring.manage',
    'proctoring.policy',
    'analytics.read',
    'comm.read',
    'announcements.manage',
    'chat.moderate',
    'ptm.manage',
    'hr.read',
    'leaves.request',
    'approvals.request',
    'taxonomy.manage',
    'lms.read',
    'lms.author',
    'sis.read',
    'sched.read',
  ],
  counselor: [
    'core.dashboard.read',
    'core.directory.read',
    'core.directory.manage',
    'core.batches.read',
    'core.inquiries.read',
    'core.inquiries.manage',
    'core.attendance.read',
    'core.feebook.read',
    'core.notices.read',
    'core.notices.manage',
    'students.read',
    'students.write',
    'sis.directory.manage',
    'sis.tuition.use',
    'crm.read',
    'leads.manage',
    'crm.manage',
    'schedule.read',
    'batches.manage',
    'attendance.read',
    'fees.read',
    'comm.read',
    'announcements.manage',
    'broadcasts.send',
    'ptm.manage',
    'hr.read',
    'leaves.request',
    'ops.read',
    'approvals.request',
    'visitors.manage',
    'webbuilder.view',
    'webbuilder.leads',
    'webbuilder.page.read',
    'sis.read',
  ],
  accountant: [
    'core.dashboard.read',
    'core.directory.read',
    'core.feebook.read',
    'core.feebook.manage',
    'core.attendance.read',
    'core.notices.read',
    'students.read',
    'fees.read',
    'fees.accounting.read',
    'fees.manage',
    'fees.collect',
    'fees.refund.approve',
    'comm.read',
    'broadcasts.send',
    'hr.read',
    'hr.payroll.read',
    'leaves.request',
    'payroll.manage',
    'ops.read',
    'approvals.request',
    'approvals.decide',
    'assets.manage',
    'bi.read',
    'reports.run',
    'reports.manage',
    'webbuilder.view',
    'webbuilder.page.read',
  ],
  student: [
    'core.dashboard.read',
    'core.batches.read',
    'core.attendance.read',
    'core.study.read',
    'core.feebook.read',
    'core.notices.read',
    'students.read',
    'sis.tuition.use',
    'schedule.read',
    'attendance.read',
    'courses.read',
    'live.join',
    'tests.take',
    'fees.read',
    'comm.read',
    'ptm.manage',
    'crm.read',
    'lms.read',
    'sis.read',
  ],
  parent: [
    'core.dashboard.read',
    'core.batches.read',
    'core.attendance.read',
    'core.study.read',
    'core.feebook.read',
    'core.notices.read',
    'students.read',
    'sis.tuition.use',
    'schedule.read',
    'attendance.read',
    'courses.read',
    'live.join',
    'tests.take',
    'fees.read',
    'comm.read',
    'ptm.manage',
    'crm.read',
    'lms.read',
    'sis.read',
  ],
};

export const AuthContext = createContext<AuthState | undefined>(undefined);

export function hasPermissionCheck(granted: Record<string, boolean>, required: string): boolean {
  if (!granted || !required) return false;
  if (granted['*']) return true;
  if (granted[required]) return true;

  const parts = required.split('.');
  if (parts.length >= 2) {
    const wildcard = `${parts[0]}.*`;
    if (granted[wildcard]) return true;
  }
  return false;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const queryClient = useQueryClient();
  const [localRole, setLocalRole] = useState<Role>(
    (localStorage.getItem('hc_role') as Role) || 'owner'
  );

  useEffect(() => {
    localStorage.setItem('hc_role', localRole);
  }, [localRole]);

  const { data: meData, isLoading } = useQuery<MeResponse | null>({
    queryKey: ['auth', 'me', localRole],
    queryFn: async () => {
      try {
        return await coachApi.get<MeResponse>('/auth/me');
      } catch (err: unknown) {
        return null;
      }
    },
    retry: false,
    staleTime: 60000,
  });

  const loginMutation = useMutation({
    mutationFn: async ({ email, pass }: { email: string; pass: string }) => {
      const resp = await coachApi.post<{
        tokens?: { access_token: string; refresh_token: string };
        user?: { id: string; email: string; role?: Role };
        csrf_token?: string;
      }>('/auth/login', { email, password: pass });

      if (resp.tokens?.access_token) {
        localStorage.setItem('hc_token', resp.tokens.access_token);
      }
      if (resp.tokens?.refresh_token) {
        localStorage.setItem('hc_refresh_token', resp.tokens.refresh_token);
      }
      if (resp.user?.role) {
        setLocalRole(resp.user.role);
      }
      if (resp.csrf_token) {
        setCSRFToken(resp.csrf_token);
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'me'] });
    },
  });

  const logoutMutation = useMutation({
    mutationFn: async () => {
      try {
        const refreshToken = localStorage.getItem('hc_refresh_token');
        await coachApi.post('/auth/logout', { refresh_token: refreshToken });
      } catch {
        // Continue even if network error
      }
      localStorage.removeItem('hc_token');
      localStorage.removeItem('token');
      localStorage.removeItem('hc_refresh_token');
      setCSRFToken(null);
    },
    onSuccess: () => {
      queryClient.setQueryData(['auth', 'me'], null);
      queryClient.clear();
      window.location.href = '/login';
    },
  });

  const [activeStudentId, setActiveStudentId] = useState<string>(() => {
    try {
      return localStorage.getItem('hc_active_student_id') || '';
    } catch (_) {
      return '';
    }
  });

  const familyContext = meData?.family_context;
  const currentActiveId = activeStudentId || familyContext?.active_student_id || '';

  const activeStudent = React.useMemo(() => {
    if (!familyContext?.wards || familyContext.wards.length === 0) return undefined;
    return familyContext.wards.find((w) => w.student_id === currentActiveId) || familyContext.wards[0];
  }, [familyContext, currentActiveId]);

  const switchActiveStudent = React.useCallback(
    (studentId: string) => {
      setActiveStudentId(studentId);
      try {
        localStorage.setItem('hc_active_student_id', studentId);
      } catch (_) {}
      queryClient.invalidateQueries();
    },
    [queryClient]
  );

  const isAuthenticated = Boolean(meData?.user_id);

  const user: User | null = meData?.user_id
    ? {
        id: meData.user_id,
        email: meData.email || '',
        name: meData.full_name || (meData.email ? meData.email.split('@')[0] : (meData.role || localRole)),
        role: meData.role || localRole,
        tenant_id: meData.tenant?.id,
        family_context: meData.family_context,
      }
    : null;

  const permissions: Record<string, boolean> = meData?.permissions || {};

  const enabledSuites = meData?.enabled_suites && meData.enabled_suites.length > 0
    ? meData.enabled_suites
    : ['core', 'platform'];

  const enabledModules = meData?.enabled_modules && meData.enabled_modules.length > 0
    ? meData.enabled_modules
    : ['core/*', 'platform/branding', 'platform/suites-manager'];

  const planTier = meData?.plan_tier || 'BASIC';

  const tenant: Tenant | null = meData?.tenant || null;

  const suiteSet = React.useMemo(() => new Set(enabledSuites), [enabledSuites]);
  const moduleSet = React.useMemo(() => new Set(enabledModules), [enabledModules]);

  // Down-scope permissions if localRole is set to a restricted role (e.g. previewing student, teacher)
  const effectivePermissions = React.useMemo(() => {
    if (localRole === 'owner' || localRole === 'admin' || localRole === 'branch_head') {
      return permissions;
    }
    const allowed = ROLE_DOWN_SCOPE_CAPABILITIES[localRole] || [];
    const downscoped: Record<string, boolean> = {};
    for (const cap of allowed) {
      if (permissions['*'] || permissions[cap]) {
        downscoped[cap] = true;
      }
    }
    // Also retain any explicitly granted custom permissions from backend
    for (const [permKey, isGranted] of Object.entries(permissions)) {
      if (isGranted) {
        downscoped[permKey] = true;
      }
    }
    return downscoped;
  }, [permissions, localRole]);

  const hasPermission = React.useCallback(
    (perm: string) => hasPermissionCheck(effectivePermissions, perm),
    [effectivePermissions]
  );

  const hasSuite = React.useCallback(
    (suiteId: string) => {
      if (suiteId === 'core' || suiteId === 'platform') return true;
      return suiteSet.has(suiteId);
    },
    [suiteSet]
  );

  const hasModule = React.useCallback(
    (suiteId: string, moduleId?: string) => {
      if (suiteId === 'core') return true;
      if (suiteId === 'platform' && (moduleId === 'branding' || moduleId === 'suites-manager')) return true;
      if (!hasSuite(suiteId)) return false;
      if (!moduleId) return true;
      if (moduleSet.has('*') || moduleSet.has(`${suiteId}/*`)) return true;
      return moduleSet.has(`${suiteId}/${moduleId}`);
    },
    [hasSuite, moduleSet]
  );

  const value: AuthState = {
    user,
    tenant,
    role: localRole,
    setRole: (r: Role) => setLocalRole(r),
    permissions: effectivePermissions,
    enabledSuites,
    enabledModules,
    planTier,
    isAuthenticated,
    isLoading,
    familyContext,
    activeStudent,
    switchActiveStudent,
    login: async (email, pass) => {
      await loginMutation.mutateAsync({ email, pass });
    },
    logout: async () => {
      await logoutMutation.mutateAsync();
    },
    hasPermission,
    hasSuite,
    hasModule,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
